import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDownToLine,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  Pause,
  Play,
  RefreshCw,
  ScrollText,
  Search,
  WrapText,
} from "lucide-react";
import { useGetServerLogsQuery } from "../store";
import { ApiErrorText, extractLogMessage, isUnexpectedErrorMessage } from "../components/ApiErrorText";
import {
  LOGGER_FILTER_ALL,
  LOGGER_FILTER_NONE,
  PAGE_FILTER_ALL,
  PAGE_FILTER_NONE,
  CRON_FILTER_ALL,
  CRON_FILTER_ANY,
  CRON_JOB_FILTERS,
  DATE_FILTER_ALL,
  SCHEDULED_LOG_USER,
  USER_FILTER_ALL,
  USER_FILTER_NONE,
  cronFilterLabel,
  formatLogPageOptionLabel,
  groupLogEvents,
  isCronLogPage,
  localIsoDate,
  loggerFilterLabel,
  pageFilterLabel,
  serverLogDateLabel,
  serverLogDayOptions,
  userFilterLabel,
} from "../utils/serverLogPageFilters";

const LEVEL_ALL = "all";
const LEVEL_ERROR = "ERROR";
const LEVEL_WARN = "WARN";
const LEVEL_INFO_WARN_DEBUG = "INFO_WARN_DEBUG";

function formatLoadError(err) {
  if (!err) {
    return "Unable to load server logs.";
  }
  if (err.status === 403) {
    return "You do not have permission to view server logs.";
  }
  const detail = err?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }
  if (typeof err?.error === "string" && err.error.trim()) {
    return err.error.trim();
  }
  if (err?.status === "FETCH_ERROR") {
    return "Network error. Check that the API server is running.";
  }
  if (typeof err?.status === "number") {
    return `Unable to load server logs (HTTP ${err.status}).`;
  }
  return "Unable to load server logs.";
}

function normalizedLevel(level) {
  return String(level ?? "").trim().toUpperCase();
}

function isErrorLevel(level) {
  return normalizedLevel(level) === "ERROR";
}

function isWarnLevel(level) {
  return normalizedLevel(level) === "WARN";
}

function lineClassName(level) {
  if (isErrorLevel(level)) {
    return "block font-bold text-red-600 dark:text-red-400";
  }
  if (isWarnLevel(level)) {
    return "block font-semibold text-amber-700 dark:text-amber-300";
  }
  return "block";
}

function filterSummary(dateLabel, levelFilter, pageFilter, cronFilter, userFilter, loggerFilter, search) {
  const pageLabel = pageFilterLabel(pageFilter);
  const cronLabel = cronFilterLabel(cronFilter);
  const userLabel = userFilterLabel(userFilter);
  const loggerLabel = loggerFilterLabel(loggerFilter);
  const pageSuffix = pageLabel ? ` for ${pageLabel}` : "";
  const cronSuffix = cronLabel ? ` for ${cronLabel}` : "";
  const userSuffix = userLabel ? ` by ${userLabel}` : "";
  const loggerSuffix = loggerLabel ? ` in ${loggerLabel}` : "";
  const searchSuffix = search.trim() ? ` matching “${search.trim()}”` : "";
  if (levelFilter === LEVEL_ERROR) {
    return `Error log statements for ${dateLabel}${cronSuffix}${pageSuffix}${userSuffix}${loggerSuffix}${searchSuffix}. Stack traces are collapsed by default.`;
  }
  if (levelFilter === LEVEL_WARN) {
    return `Warning log statements for ${dateLabel}${cronSuffix}${pageSuffix}${userSuffix}${loggerSuffix}${searchSuffix}.`;
  }
  if (levelFilter === LEVEL_INFO_WARN_DEBUG) {
    return `INFO, WARN, and DEBUG log statements for ${dateLabel}${cronSuffix}${pageSuffix}${userSuffix}${loggerSuffix}${searchSuffix}.`;
  }
  return `All backend log statements for ${dateLabel}${cronSuffix}${pageSuffix}${userSuffix}${loggerSuffix}${searchSuffix}. Errors are bold red; warnings are amber.`;
}

function lineCountLabel(displayedCount, matchedCount, omittedCount, truncated) {
  const matching = Number.isFinite(matchedCount) ? matchedCount : displayedCount;
  if (truncated && omittedCount > 0) {
    return `Showing ${displayedCount.toLocaleString()} of ${matching.toLocaleString()} matching line${
      matching === 1 ? "" : "s"
    } (${omittedCount.toLocaleString()} older omitted)`;
  }
  return `${displayedCount.toLocaleString()} matching line${displayedCount === 1 ? "" : "s"}`;
}

function emptyFilterMessage(hasAnyFacets, levelFilter, allDays) {
  if (!hasAnyFacets) {
    return allDays
      ? "No log statements recorded in the last 14 days."
      : "No log statements recorded for this date.";
  }
  if (levelFilter === LEVEL_ERROR) {
    return "No error log statements match the current filters.";
  }
  if (levelFilter === LEVEL_WARN) {
    return "No warning log statements match the current filters.";
  }
  if (levelFilter === LEVEL_INFO_WARN_DEBUG) {
    return "No INFO/WARN/DEBUG log statements match the current filters.";
  }
  return "No log statements match the current filters.";
}

function eventKey(event, index) {
  const first = event[0];
  const text = typeof first?.text === "string" ? first.text.slice(0, 80) : "";
  return `${index}:${text}`;
}

function linesToPlainText(events, expandedKeys) {
  const out = [];
  events.forEach((event, index) => {
    const key = eventKey(event, index);
    const expanded = event.length === 1 || expandedKeys.has(key);
    const visible = expanded ? event : [event[0]];
    for (const line of visible) {
      out.push(typeof line?.text === "string" ? line.text : String(line ?? ""));
    }
  });
  return out.join("\n");
}

const selectClassName =
  "px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50 max-w-[16rem]";

function ServerLogs() {
  const today = localIsoDate();
  const [date, setDate] = useState(DATE_FILTER_ALL);
  const dayOptions = useMemo(() => serverLogDayOptions(today), [today]);
  const [levelFilter, setLevelFilter] = useState(LEVEL_ERROR);
  const [pageFilter, setPageFilter] = useState(PAGE_FILTER_ALL);
  const [cronFilter, setCronFilter] = useState(CRON_FILTER_ALL);
  const [userFilter, setUserFilter] = useState(USER_FILTER_ALL);
  const [loggerFilter, setLoggerFilter] = useState(LOGGER_FILTER_ALL);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [live, setLive] = useState(true);
  const [wrapLines, setWrapLines] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [copyState, setCopyState] = useState("");
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const scrollerRef = useRef(null);
  const stickToBottomRef = useRef(true);

  useEffect(() => {
    const handle = window.setTimeout(() => setSearchQuery(searchInput), 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const allDays = date === DATE_FILTER_ALL;
  const isToday = allDays || date === today;
  const cronSelected = cronFilter !== CRON_FILTER_ALL;
  const queryArgs = useMemo(
    () => ({
      date: date || DATE_FILTER_ALL,
      level: levelFilter,
      page: cronSelected
        ? cronFilter
        : pageFilter === PAGE_FILTER_ALL
          ? undefined
          : pageFilter,
      user: userFilter === USER_FILTER_ALL ? undefined : userFilter,
      logger: loggerFilter === LOGGER_FILTER_ALL ? undefined : loggerFilter,
      q: searchQuery.trim() || undefined,
    }),
    [cronFilter, cronSelected, date, levelFilter, loggerFilter, pageFilter, searchQuery, userFilter]
  );

  const { data, isLoading, isFetching, isError, error, refetch } = useGetServerLogsQuery(queryArgs, {
    pollingInterval: live && isToday ? 8000 : 0,
  });
  const loadErrorMessage = formatLoadError(error);

  const lines = useMemo(() => (Array.isArray(data?.lines) ? data.lines : []), [data?.lines]);
  const events = useMemo(() => groupLogEvents(lines), [lines]);
  const pageOptions = useMemo(() => {
    const fromApi = (Array.isArray(data?.pages) ? data.pages : []).filter((page) => !isCronLogPage(page));
    const merged = [...fromApi];
    if (
      pageFilter &&
      pageFilter !== PAGE_FILTER_ALL &&
      pageFilter !== PAGE_FILTER_NONE &&
      !isCronLogPage(pageFilter) &&
      !merged.includes(pageFilter)
    ) {
      merged.push(pageFilter);
    }
    return merged;
  }, [data?.pages, pageFilter]);
  const userOptions = useMemo(() => {
    const fromApi = Array.isArray(data?.users) ? data.users : [];
    const merged = fromApi.includes(SCHEDULED_LOG_USER)
      ? [...fromApi]
      : [...fromApi, SCHEDULED_LOG_USER];
    if (
      userFilter &&
      userFilter !== USER_FILTER_ALL &&
      userFilter !== USER_FILTER_NONE &&
      !merged.includes(userFilter)
    ) {
      merged.push(userFilter);
    }
    return merged;
  }, [data?.users, userFilter]);
  const loggerOptions = useMemo(() => {
    const fromApi = Array.isArray(data?.loggers) ? data.loggers : [];
    if (
      loggerFilter &&
      loggerFilter !== LOGGER_FILTER_ALL &&
      loggerFilter !== LOGGER_FILTER_NONE &&
      !fromApi.includes(loggerFilter)
    ) {
      return [...fromApi, loggerFilter];
    }
    return fromApi;
  }, [data?.loggers, loggerFilter]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !stickToBottomRef.current) {
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [lines.length, data?.date, cronFilter, levelFilter, pageFilter, userFilter, loggerFilter, searchQuery]);

  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    const stuck = remaining < 48;
    stickToBottomRef.current = stuck;
    setShowJumpToLatest(!stuck && lines.length > 0);
  };

  const jumpToLatest = () => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }
    stickToBottomRef.current = true;
    setShowJumpToLatest(false);
    el.scrollTop = el.scrollHeight;
  };

  const toggleEvent = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleCopy = async () => {
    const text = linesToPlainText(events, expandedKeys);
    try {
      await navigator.clipboard.writeText(text);
      setCopyState("Copied");
    } catch {
      setCopyState("Copy failed");
    }
    window.setTimeout(() => setCopyState(""), 2000);
  };

  const handleDownload = () => {
    const text = linesToPlainText(events, expandedKeys);
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = allDays ? "billing-last-14-days.log" : `billing-${date}.log`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const hasFacets =
    (Array.isArray(data?.pages) && data.pages.length > 0) ||
    (Array.isArray(data?.users) && data.users.length > 0) ||
    (Array.isArray(data?.loggers) && data.loggers.length > 0) ||
    Number(data?.matchedCount ?? lines.length) > 0 ||
    lines.length > 0;

  return (
    <div className="p-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Server Logs
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {filterSummary(
              serverLogDateLabel(data?.date || date, today, data?.fromDate, data?.toDate),
              levelFilter,
              cronSelected ? PAGE_FILTER_ALL : pageFilter,
              cronFilter,
              userFilter,
              loggerFilter,
              searchQuery
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <span className="sr-only">Log date</span>
            <select
              value={date}
              onChange={(e) => {
                const next = e.target.value || DATE_FILTER_ALL;
                setDate(next);
                if (next !== DATE_FILTER_ALL && next !== today) {
                  setLive(false);
                }
              }}
              disabled={isLoading || isError}
              aria-label="Log date"
              className={selectClassName}
            >
              <option value={DATE_FILTER_ALL}>Last 14 days</option>
              {dayOptions.map((day) => (
                <option key={day} value={day}>
                  {day === today ? `${day} (today)` : day}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => setLive((prev) => !prev)}
            disabled={!isToday}
            className="app-btn-plain inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {live && isToday ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {live && isToday ? "Pause" : "Live"}
          </button>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="app-btn-gloss inline-flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="relative min-w-[16rem] flex-1">
          <span className="sr-only">Search logs</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            disabled={isLoading || isError}
            placeholder="Search logger, message, SQL, exception…"
            className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-400">
          User
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            disabled={isLoading || isError}
            aria-label="User filter"
            className={selectClassName}
          >
            <option value={USER_FILTER_ALL}>All users</option>
            <option value={USER_FILTER_NONE}>No user</option>
            {userOptions.map((name) => (
              <option key={name} value={name}>
                {name === SCHEDULED_LOG_USER ? "scheduled (cron)" : name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-400">
          Cron job
          <select
            value={cronFilter}
            onChange={(e) => {
              const next = e.target.value || CRON_FILTER_ALL;
              setCronFilter(next);
              if (next !== CRON_FILTER_ALL) {
                setPageFilter(PAGE_FILTER_ALL);
              }
            }}
            disabled={isLoading || isError}
            aria-label="Cron job filter"
            className={selectClassName}
          >
            <option value={CRON_FILTER_ALL}>All</option>
            <option value={CRON_FILTER_ANY}>Any cron job</option>
            {CRON_JOB_FILTERS.map((job) => (
              <option key={job.value} value={job.value}>
                {job.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-400">
          Page
          <select
            value={pageFilter}
            onChange={(e) => setPageFilter(e.target.value)}
            disabled={isLoading || isError || cronSelected}
            aria-label="Page filter"
            className={selectClassName}
          >
            <option value={PAGE_FILTER_ALL}>All pages</option>
            <option value={PAGE_FILTER_NONE}>No page</option>
            {pageOptions.map((page) => (
              <option key={page} value={page}>
                {formatLogPageOptionLabel(page)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-400">
          Logger
          <select
            value={loggerFilter}
            onChange={(e) => setLoggerFilter(e.target.value)}
            disabled={isLoading || isError}
            aria-label="Logger filter"
            className={selectClassName}
          >
            <option value={LOGGER_FILTER_ALL}>All loggers</option>
            <option value={LOGGER_FILTER_NONE}>No logger</option>
            {loggerOptions.map((logger) => (
              <option key={logger} value={logger}>
                {logger}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-400">
          Level
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            disabled={isLoading || isError}
            aria-label="Log level filter"
            className={selectClassName}
          >
            <option value={LEVEL_ERROR}>Errors only</option>
            <option value={LEVEL_WARN}>Warnings only</option>
            <option value={LEVEL_INFO_WARN_DEBUG}>INFO/WARN/DEBUG</option>
            <option value={LEVEL_ALL}>All levels</option>
          </select>
        </label>
      </div>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        Logs are kept for 14 days. Default view is the full window; pick a day to narrow it.{" "}
        <strong className="font-medium">No user</strong> covers startup, health checks, and unauthenticated
        requests. Signed-in users are tagged as display name (emp ID). Use{" "}
        <strong className="font-medium">Cron job</strong> to isolate scheduled jobs (monthly invoice or
        user-activity purge); set Level to INFO/WARN/DEBUG or All to see those lines.
      </p>

      {isError ? (
        <div className="rounded border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-800 dark:text-red-200 mb-6">
          <p className="font-medium">Could not load server logs</p>
          <ApiErrorText
            error={
              isUnexpectedErrorMessage(loadErrorMessage)
                ? { message: loadErrorMessage, logMessage: extractLogMessage(error) }
                : loadErrorMessage
            }
            className="mt-1 block text-red-800 dark:text-red-200"
          />
          <button
            type="button"
            onClick={() => refetch()}
            className="app-btn-gloss mt-3 px-3 py-1.5 text-sm"
          >
            Retry
          </button>
        </div>
      ) : null}

      {!isError ? (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <ScrollText className="h-4 w-4 shrink-0" />
              <span>
                {isLoading
                  ? "Loading…"
                  : lineCountLabel(
                      lines.length,
                      Number(data?.matchedCount ?? lines.length),
                      Number(data?.omittedCount ?? 0),
                      Boolean(data?.truncated)
                    )}
              </span>
              {!live || !isToday ? (
                <span className="text-amber-700 dark:text-amber-300">Paused</span>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {copyState ? (
                <span className="text-xs text-green-700 dark:text-green-300">{copyState}</span>
              ) : null}
              <button
                type="button"
                onClick={() => setWrapLines((prev) => !prev)}
                className="app-btn-plain inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <WrapText className="h-3.5 w-3.5" />
                {wrapLines ? "Unwrap" : "Wrap"}
              </button>
              <button
                type="button"
                onClick={handleCopy}
                disabled={lines.length === 0}
                className="app-btn-plain inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                <Copy className="h-3.5 w-3.5" />
                Copy
              </button>
              <button
                type="button"
                onClick={handleDownload}
                disabled={lines.length === 0}
                className="app-btn-plain inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                <Download className="h-3.5 w-3.5" />
                Download
              </button>
              {data?.source ? (
                <span className="text-xs text-gray-500 dark:text-gray-400 font-mono break-all">
                  {data.source}
                </span>
              ) : null}
            </div>
          </div>
          <div className="relative">
            <div
              ref={scrollerRef}
              onScroll={handleScroll}
              className="max-h-[calc(100vh-22rem)] overflow-auto bg-gray-50 dark:bg-gray-950 px-4 py-3"
            >
              {isLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading logs…</p>
              ) : events.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {emptyFilterMessage(hasFacets, levelFilter, allDays)}
                </p>
              ) : (
                <pre
                  className={`${
                    wrapLines ? "whitespace-pre-wrap break-words" : "whitespace-pre overflow-x-auto"
                  } font-mono text-xs leading-5 text-gray-800 dark:text-gray-200`}
                >
                  {events.map((event, index) => {
                    const key = eventKey(event, index);
                    const hasStack = event.length > 1;
                    const expanded = !hasStack || expandedKeys.has(key);
                    const visible = expanded ? event : [event[0]];
                    return (
                      <span key={key} className="block">
                        {hasStack ? (
                          <button
                            type="button"
                            onClick={() => toggleEvent(key)}
                            className="app-btn-plain mr-1 inline-flex align-middle text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                            aria-expanded={expanded}
                            aria-label={expanded ? "Collapse stack trace" : "Expand stack trace"}
                          >
                            {expanded ? (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5" />
                            )}
                          </button>
                        ) : null}
                        {visible.map((line, lineIndex) => {
                          const text = typeof line?.text === "string" ? line.text : String(line ?? "");
                          return (
                            <span
                              key={`${key}-${lineIndex}`}
                              className={`${lineClassName(line?.level)} ${hasStack && lineIndex === 0 ? "inline" : ""}`}
                            >
                              {text}
                              {hasStack && !expanded && lineIndex === 0 ? (
                                <span className="ml-2 font-sans font-normal text-gray-500 dark:text-gray-400">
                                  +{event.length - 1} stack lines
                                </span>
                              ) : null}
                            </span>
                          );
                        })}
                      </span>
                    );
                  })}
                </pre>
              )}
            </div>
            {showJumpToLatest ? (
              <button
                type="button"
                onClick={jumpToLatest}
                className="app-btn-gloss absolute bottom-3 right-3 inline-flex items-center gap-1 px-3 py-1.5 text-xs shadow"
              >
                <ArrowDownToLine className="h-3.5 w-3.5" />
                Jump to latest
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { ServerLogs };
